import { useRef, useState } from "react";
import ICONS from "../../../../assets/Icons";
import useCategories from "../../../../utils/useCategories";
import CategoriesCard from "../../CategoriesCard";
import SearchField from "../../CommonSearchComponents/SearchField";
import TickerAnimation from "../../TickerAnimation";
import SVGComponent from "../../SVGComponent";

/**
 * @param {Array} props.sortedCategories - Sorting of category array
 * @param {Array} props.selectedCategories - Array of selected category IDs
 * @param {boolean} props.isCategoryLoading - Category loading state
 * @param {string} props.Title - Main title text
 * @param {string} props.SubTitle - Subtitle text
 * @param {string} [props.TitleFontcolor] - Main title font colour
 * @param {string} [props.TextSize] - Main title font size
 * @param {string} [props.SubTitleTextSize] - Subtitle font size
 * @param {string} [props.TitleFontWeight] - Main title font weight
 */

const CategoriesEdit = ({
  type,
  sortedCategories,
  selectedCategories,
  handleCategorySelect,
  Title,
  SubTitle,
  TextSize,
  TitleFontcolor,
  SubTitleFontcolor,
  SubTitleTextSize,
  TitleFontWeight,
  SubTitleFontWeight,
  isPreview = false,
  flow,
  isCategoryLoading = false,
}) => {
  const categoryMapper = useCategories();

  const searchRef = useRef(null);

  const [searchText, setSearchText] = useState("");

  const categoriesData = Array.isArray(sortedCategories)
    ? sortedCategories.filter((item) => item?.active === "Y")
    : [];

  const filteredCategories = categoriesData.filter((category) =>
    category?.name?.toLowerCase().includes(searchText.toLowerCase()),
  );

  const hasSearch = searchText.trim().length > 0;

  if (isPreview) {
    return (
      <div className="flex flex-col bg-primary-card rounded-border-radius-md gap-gap-md py-padding-md w-width-full">
        <div className="px-padding-md">
          <div className="text-text-primary font-semibold text-font-md">
            {Title}
          </div>
        </div>

        <div className="overflow-auto max-h-category-popup-height px-padding-md w-width-full">
          <div className="flex flex-col gap-gap-md w-width-full">
            {selectedCategories?.map((categoryId) => {
              const category = categoriesData.find(
                (item) => item?.id === categoryId,
              );
              if (!category) {
                return null;
              }
              const categoryIcon = categoryMapper(category?.id);

              return (
                <div
                  key={categoryId}
                  className="bg-primary-nav rounded-border-radius-md flex w-width-full"
                >
                  <div className="p-padding-sm flex items-center bg-text-primary rounded-l-border-radius-md">
                    <div className="w-icon-lg h-icon-lg flex-shrink-0">
                      <SVGComponent
                        url={categoryIcon?.url}
                        color={categoryIcon?.colour}
                        height="var(--icon-lg)"
                        width="var(--icon-lg)"
                        alt={category?.name}
                      />
                    </div>
                  </div>

                  <div className="w-full flex flex-col justify-center pl-padding-sm min-w-0">
                    <p className="text-text-primary truncate text-font-sm font-semibold">
                      <TickerAnimation text={category?.name} />
                    </p>

                    {category?.description && (
                      <p className="text-text-secondary text-font-xs truncate">
                        <TickerAnimation text={category.description} />
                      </p>
                    )}
                  </div>

                  <div className="px-padding-sm" />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className={`bg-primary-card py-padding-md pl-padding-md rounded-border-radius-md relative ${
          flow === "switch_account"
            ? "opacity-light-opacity"
            : "opacity-base-opacity"
        }`}
      >
        <div>
          <p
            className={`text-${TitleFontcolor} text-${TextSize} font-${TitleFontWeight}`}
          >
            {Title}
          </p>
          <p
            className={`text-${SubTitleFontcolor} text-${SubTitleTextSize} font-${SubTitleFontWeight} mt-margin-xs mr-margin-md`}
          >
            {SubTitle}
          </p>
        </div>
        <div className="mt-margin-md mr-margin-md">
          <SearchField
            searchText={searchText}
            searchRef={searchRef}
            placeholder="Search categories"
            onInput={(value) => setSearchText(value)}
            isDisabled={flow === "switch_account" ? true : false}
            searchBgColor="var(--primary-bg)"
          />
        </div>
        <div className="max-h-category-popup-height overflow-y-auto mt-margin-md gap-gap-md flex flex-col">
          {isCategoryLoading ? (
            <div className="flex justify-center items-center py-padding-lg">
              <img
                src={ICONS.LOADER_GIF}
                alt="Loading"
                className="h-icon-lg w-icon-lg animate-spin"
              />
            </div>
          ) : filteredCategories.length > 0 ? (
            filteredCategories.map((category) => {
              const isSelected = selectedCategories?.includes(category?.id);

              return (
                <CategoriesCard
                  key={category?.id}
                  handledata={category}
                  selectedBackgroundColour={
                    isSelected ? "positive-bg" : "primary-nav"
                  }
                  handleCategorySelect={
                    type === "CategoryCreate"
                      ? () => handleCategorySelect(category)
                      : handleCategorySelect
                  }
                  logoIcon={
                    isSelected
                      ? ICONS?.SELECTED_WITH_GREEN_CIRCLE
                      : ICONS.SELECT_ALL_WITH_WHITE_OUTLINE_CIRCLE
                  }
                  categoryNameColour="text-primary"
                  categoryTextSize="font-sm"
                  categoryFontWeight="semibold"
                  flow={flow}
                />
              );
            })
          ) : hasSearch ? (
            <div className="flex items-center justify-start">
              <p className="text-text-secondary text-font-xs">
                No category found
              </p>
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
};

export default CategoriesEdit;
